import { InfoIcon, CheckCircleIcon, WarningIcon, XCircleIcon, XIcon } from '@phosphor-icons/react'
import styles from './ScoopAlert.module.scss'

const SEVERITY_ICONS = {
    info: InfoIcon,
    success: CheckCircleIcon,
    warning: WarningIcon,
    error: XCircleIcon,
}

/**
 * ScoopAlert - A feedback component for displaying important messages to users
 *
 * @component
 * @param {Object} props - Component props
 * @param {'info' | 'success' | 'warning' | 'error'} [props.severity='info'] - The severity of the alert
 *   - info: General information (blue)
 *   - success: Success message (green)
 *   - warning: Warning message (orange/yellow)
 *   - error: Error message (red)
 * @param {'default' | 'outlined'} [props.variant='default'] - The visual style variant
 *   - default: Filled background with light tint
 *   - outlined: White/transparent background with colored border
 * @param {React.ReactNode} [props.icon] - Custom icon to display. Pass `null` to hide the icon entirely.
 *   If not provided, a default icon based on severity is shown.
 * @param {React.ReactNode} [props.action] - Action element to display on the right side (e.g., button, link)
 * @param {function} [props.onClose] - Callback fired when the close button is clicked. If provided, shows a close icon.
 * @param {React.ReactNode} props.children - The content of the alert
 * @param {string} [props.className] - Additional CSS classes to apply
 * @param {Object} [props.style] - Inline styles (highest priority)
 * @param {...any} props - All other props are passed to the root div element
 *
 * @example
 * // Basic usage
 * <ScoopAlert severity="success">Operation completed successfully!</ScoopAlert>
 *
 * @example
 * // With close button
 * <ScoopAlert severity="error" onClose={() => setOpen(false)}>Something went wrong</ScoopAlert>
 *
 * @example
 * // Outlined variant with action
 * <ScoopAlert
 *   severity="warning"
 *   variant="outlined"
 *   action={<ScoopButton size="sm">Retry</ScoopButton>}
 * >
 *   Connection lost
 * </ScoopAlert>
 *
 * @example
 * // Custom icon
 * <ScoopAlert severity="info" icon={<BellIcon size={20} weight="fill" />}>
 *   You have new notifications
 * </ScoopAlert>
 *
 * @example
 * // No icon
 * <ScoopAlert severity="info" icon={null}>Simple message without icon</ScoopAlert>
 */
export const ScoopAlert = ({
    severity = 'info',
    variant = 'default',
    icon,
    action,
    onClose,
    children,
    className = '',
    style,
    ...props
}) => {
    const alertClassName = [
        styles.alert,
        styles[severity] || styles.info,
        styles[variant] || styles.default,
        className,
    ]
        .filter(Boolean)
        .join(' ')

    // Determine which icon to show
    const renderIcon = () => {
        // If icon is explicitly null, don't show any icon
        if (icon === null) return null

        // If a custom icon is provided, use it
        if (icon) return <span className={styles.icon}>{icon}</span>

        // Otherwise, use the default icon for this severity
        const DefaultIcon = SEVERITY_ICONS[severity] || SEVERITY_ICONS.info
        return (
            <span className={styles.icon}>
                <DefaultIcon size={20} weight="fill" />
            </span>
        )
    }

    return (
        <div className={alertClassName} style={style} role="alert" {...props}>
            {renderIcon()}
            <div className={styles.content}>{children}</div>
            {action && <div className={styles.action}>{action}</div>}
            {onClose && (
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close alert"
                >
                    <XIcon size={18} weight="bold" />
                </button>
            )}
        </div>
    )
}

export default ScoopAlert
